import {Inject, Injectable, PLATFORM_ID} from '@angular/core';
import {from, Observable, of, timer} from 'rxjs';
import {catchError, finalize, first, map, shareReplay, switchMap, take, tap} from 'rxjs/operators';
import {Select, Store} from '@ngxs/store';
import {countryConfig} from 'src/country-config/country-config';
import {SubstitutesRes} from '../../networking/models/cart/responses/substitutes-res';
import {CartService} from '../../networking/services/cart/cart.service';
import {LocalStorageManager} from '../local-storage/local-storage.manager';
import {CartRes} from '../../networking/models/cart/responses/cart-res';
import {
  DeleteCartAction,
  RemoveCartProductAction,
  SaveCartAction,
  UpdateCartProductAction,
} from '../../actions/cart.actions';
import {CartDataUtil} from 'src/app/ui/shared/utilities/cart-data/cart-data.util';
import {AlgoliaManager} from '../algolia/algolia.manager';
import {Product} from '../../networking/models/product/responses/product.class';
import {LocationManager} from '../location/location.manager';
import {AddressManager} from '../customer/address/address.manager';
import {DeliveryTypeEnum} from '../../networking/models/location/enum/delivery-type.enum';
import {SessionStorageManager} from '../session-storage/session-storage.manager';
import {UpdateProductToastEvent} from '../../../ui/shared/event-listeners/update-product-toast/update-product-toast.event';
import {UpdateOnlineOnlyModalEvent} from '../../../ui/shared/event-listeners/update-online-only-modal/update-online-only-modal.event';
import {GoogleTagService} from '../../networking/services/shared/google-tag/google-tag.service';
import {ToastEvent} from '../../../ui/shared/event-listeners/toast/toast.event';
import { TalonOneService } from '../../networking/services/talon-one/talon-one.service';
import { DeleteProduct, GetCart, CheckoutRequest } from '../../networking/models/cart/requests/cart';
import { Credits } from '../../networking/models/farmacredits/credits/credits';
import { Customer } from '../../networking/models/customer/customer';
import { isPlatformBrowser } from '@angular/common';
import { SourceEnum } from '../../networking/models/base-service/enum/base-service.enum';
import { TipResponse } from '../../networking/models/tip/responses/tip';
import { UpdateProductAdvertisedEvent } from 'src/app/ui/shared/event-listeners/update-product-advertised/update-product-advertised.event';
import { OperationalService } from '../../networking/services/operational/operational.service';
import { ResponseServiceOperationalInterface } from '../../networking/models/operational/operational';
import {CustomerAddress} from '../../networking/models/marketplace-courier/requests/shipping-req';
import { UpdateCartEvent } from 'src/app/ui/shared/event-listeners/update-cart/update-cart.event';
import { ScheduleConfig } from '../../networking/models/schedule/schedule';

@Injectable({
  providedIn: 'root',
})
export class CartManager {
  public defaultPaymentCardId: number = 0;
  @Select(state => state.cartStore) cart$: Observable<any>;
  public farmacredits: number;
  public paymentCardId: number;
  public tipValue: number;
  public source: string = 'WEB';
  public isBrowser: boolean;
  private defaultFarmaCredits = 0;

  constructor(
    private cartService: CartService,
    private lsm: LocalStorageManager,
    private ssm: SessionStorageManager,
    private store: Store,
    private algoliaManager: AlgoliaManager,
    private locationManager: LocationManager,
    private addressManager: AddressManager,
    private productToastEvent: UpdateProductToastEvent,
    private onlineOnlyEvent: UpdateOnlineOnlyModalEvent,
    private gtmService: GoogleTagService,
    private talonOne: TalonOneService,
    private toastEvent: ToastEvent,
    @Inject(PLATFORM_ID) platformId,
    private operationalService: OperationalService,
    private updateProductAdvertisedEvent: UpdateProductAdvertisedEvent,
    private cartEvent: UpdateCartEvent,
    private cartDataUtil: CartDataUtil,
  ) {
      this.isBrowser = isPlatformBrowser(platformId);
      if (this.isBrowser) this.source = window?.screen?.width <= 991 ? SourceEnum.RESPONSIVE : SourceEnum.WEB;
    }

  getCartData(data?:CheckoutRequest, address?: CustomerAddress) {
    let customer = this.lsm.get('customer');
    const cart = this.ssm.get('cart');
    const mobileSaG = this.lsm.get('mobileSaG');
    const callCenterCustomer = this.lsm.get('customerCallCenter');
    const paymentCardId = Number(this.ssm.get('paymentCardId')) || data?.paymentCardId || cart?.paymentCardId;

    if(data){
      const {farmaCredits, tip, paymentCardId} = data;
      this.farmacredits = farmaCredits;
      this.tipValue = tip;
      this.paymentCardId = paymentCardId;
    }

    customer = callCenterCustomer || customer;
    if (!customer) {
      return null;
    }
    const deliveryType = this.locationManager.getDeliveryType();
    if(deliveryType === DeliveryTypeEnum.ENVIALOYA){
      address = undefined;
    }
    const dataCart: GetCart = {
      deliveryType: mobileSaG ? DeliveryTypeEnum.SCANANDGO : deliveryType,
      id: customer ? customer.id : 0,
      idCustomerWebSafe: customer.idCustomerWebSafe,
      idStoreGroup: this.locationManager.getIdStoreGroup(),
      source: countryConfig.generalEnv.source,
      token: customer.token.token,
      tokenIdWebSafe: customer.token.tokenIdWebSafe,
      paymentCardId: Number(paymentCardId || this.defaultPaymentCardId),
      talonOneData: {
        paymentCardId: paymentCardId || this.defaultPaymentCardId,
        farmaCredits: this.farmacredits || this.defaultFarmaCredits, 
        storeId: customer.idStoreGroup, 
        source: this.source, 
        isCreateOrder: false,
        deliveryType: this.locationManager.getDeliveryType(),
      },
      ...(address || {})
    };

    if(this.tipValue || this.tipValue === 0){
      dataCart.tip = this.tipValue;
    }
    return dataCart;
  }

  get generalConfig() {
    const config = this.ssm.get('multiCountry');
    return config?.general;
  }

  saveCartAction(data) {
    this.store.dispatch(new SaveCartAction(data)).subscribe();
  }

  deleteCartAction() {
    const cart = this.ssm.get('cart');
    this.store.dispatch(new DeleteCartAction()).subscribe();
    if (cart?.itemList) {
      cart.itemList = cart?.itemList.map(item => {
        const data = {
          product: item,
          deliveryType: cart.deliveryType,
          list: 'Carrito de compras',
          preprocessor: 'product',
          cart: [],
        };
        setTimeout(() => {
          this.gtmService.onRemoveFromCart(data);
        }, 3000);
      });
    }
    this.ssm.removeItem('cart');
  }

  updateCartAction(type, data) {
    if (type === 'remove') {
      this.store.dispatch(new RemoveCartProductAction(data.product)).subscribe();
      this.toastEvent.updateToast({
        success: true,
        message: 'Producto eliminado satisfactoriamente',
      });
      //Notifica a GTM que se elimino un producto
      this.gtmService.onRemoveFromCart(data);
    } else {
      const {product, quantity} = data;
      this.store.dispatch(new UpdateCartProductAction(product, quantity)).subscribe();
      if (data.action === 'update') {
        this.gtmService.onRemoveFromCart(data);
      }
    }
    setTimeout(()=>this.cartEvent.changeCart(true),3000);
  }

  get(data?:CheckoutRequest, address?:CustomerAddress): Observable<CartRes> {
    let customer: Customer = this.lsm.get('customer');
    const callCenterCustomer: Customer = this.lsm.get('customerCallCenter');
    customer ||= callCenterCustomer;
    const cartData = this.getCartData(data, address);
    let coupon;
    const defaultTip = 0;
    const defaultPrimeDeliveryValue = 0;

    const nearbyStores = this.locationManager.getNearbyStores();

    if (customer && cartData) {

      const dataCartWithNearby = {...cartData, nearbyStores}

      return this.cartService.get(dataCartWithNearby).pipe(
        finalize(() => {
          this.farmacredits = null; 
          this.paymentCardId = null;
        }),
        map(response => {
          let cart = this.store.snapshot()?.cartStore?.cart;
          let itemList = response?.itemList;
          this.saveCartAction({...cart, itemList});
           this.ssm.set('productsSubstitutes', itemList);

          if (this.validateProductsCart(response)) {
            let hasCoupon = false;
            response.carts = [];
            response.deliveryType = cartData.deliveryType;
            response.isProvider = false;
            response.isHighDemand = false;
            if (response?.itemList) {
              if (this.generalConfig.coupons) {
                coupon = response?.itemList?.find((item: any) => item.coupon);
                if (coupon) {
                  hasCoupon = true;
                }
              }
              response.itemList = response.itemList
                ?.filter(item => !item?.coupon)
                ?.map(item => new Product(item));

              const ftdCart = {
                itemList: response?.itemList,
                isProvider: false,
                isExpress: true,
                isFtd: true,
                itemQuantity: response.quantityFarmatodo || response.itemList?.length,
                deliveryLabels: response.deliveryTimeLabel?.deliveryTimeLabelWeb,
                deliveryType: response.deliveryType,
              };

              response.carts = [...response.carts, ftdCart];
            }
            const shippingCostTotal = response.shoppingCartCourierCost?.shippingCostTotal || 0;
            const providerDeliveryPrice = response.providerDeliveryPrice || 0;
            response.billing = {
              delivery: [
                {
                  value: providerDeliveryPrice + shippingCostTotal,
                  isProvider: true,
                },
              ],
              savings: response.offerPrice,
              subTotal: response.subTotalPrice,
              total: response.totalPrice,
              deliveryLabels: response.deliveryTimeLabel
                ? response.deliveryTimeLabel.deliveryTimeLabelWeb
                : undefined,
              deliveryType: response.deliveryType,
              quantityFarmatodo: response.quantityFarmatodo || response?.itemList?.length,
              quantityProviders: response.quantityProviders || 0,
              totalTaxes: response.totalTaxes || 0,
              minimumFD: response.freeDeliveryRange?.minimumValueFreeDelivery || 0,
              missingPercentageFD: response.freeDeliveryRange?.missingPercentageFreeDelivery || 0,
              missingValueFD: response.freeDeliveryRange?.missingValueFreeDelivery || 0,
              tipPrice: response.tipPrice || defaultTip,
              primeDeliveryValue: response.primeDeliveryValue || defaultPrimeDeliveryValue,
              primeDiscountFlag: response.primeDiscountFlag,
              couponAutomaticTalonList: response.couponAutomaticTalonList,
              totalSaveProducts: response.deductDiscount?.totalSaveProducts,
              discountProductsPrime: response.deductDiscount?.discountProductsPrime,
            };

            if (response.usedCredits) {
              response.billing.farmacredits = response.usedCredits;
            }
            
            const isCartExpress = response.carts.filter(cart => cart.isExpress).length > 0;
            if (isCartExpress) {
              response.billing.delivery.push({value: response.deliveryPrice, isDefault: true});
            }

            if (response.providerList && response.providerList.length > 0) {
              if (!coupon) {
                coupon = response.providerList.map(
                  list => list?.itemList.filter(item => item.coupon)[0],
                )[0];
              }
              if (coupon) {
                hasCoupon = true;
              }

              response.providerList = response.providerList.map(list => {
                list.itemList = list?.itemList
                  .filter(item => !item.coupon)
                  .map(item => new Product(item));
                return list;
              });

              const providerCart = {
                itemList: response.providerList.flatMap(list =>
                  list?.itemList.flatMap(item => item),
                ),
                isProvider: true,
                isExpress: false,
                itemQuantity: response.providerList.reduce(
                  (accumulator, currentValue) => accumulator + currentValue.quantityItem,
                  0,
                ),
                deliveryLabels: response.deliveryTimeLabel
                  ? response.deliveryTimeLabel.deliveryTimeLabelWeb
                  : undefined,
              };
              response.carts = [...response.carts, providerCart];

              response.isProvider = true;
            }

            if (response.topRangeWeight) {
              response.breakpoints = {
                unit: 'kg',
                values: [response.topRangeWeight - 1, Math.ceil(response.weight)],
                currentValue: response.weight,
              };
            }
            response.hasCoupon = hasCoupon;

            if (coupon) {
              response.billing.coupon = {
                nameCoupon: coupon?.mediaDescription,
              };
            }

            if (response.coupon) {
              response.billing.coupon = {...response.coupon};
            }

            return response;
          }
          return null;
        }),
        tap(cart => {
          cart.paymentCardId = this.ssm.get('cart')
            ? this.ssm.get('cart')?.paymentCardId
            : this.defaultPaymentCardId;
          this.ssm.set('cart', cart);
        }),
      );
    }
    return of(null);
  }

  validateProductsCart(items) {
    return items.totalQuantity > 0 || items.itemList?.length > 0;
  }

  getAgile(force?): Observable<any> {
    let customer = this.lsm.get('customer');
    const callCenterCustomer = this.lsm.get('customerCallCenter');
    customer = callCenterCustomer || customer;
    const cartData = this.getCartData();
    if (!customer || !cartData || !cartData.deliveryType || !cartData.idStoreGroup) {
      return of(null);
    }
    return this.cart$.pipe(
      take(1),
      switchMap(response => {
        if (force || !response?.cart) {
          return this.cartService.getAgile(cartData).pipe(
            map(cart => {
              if (cart.itemList) {
                cart.itemList = cart.itemList
                  ?.filter(item => !item?.coupon)
                  ?.map(item => new Product(item));
              }
              return cart;
            }),
            tap(cart => this.saveCartAction(cart)),
            shareReplay(1),
          );
        }
        return of(response.cart);
      }),
    );
  }

  addProduct(data, body?): Observable<any> {
    let cartProducts = [];
    data.product.id = data.product.id.toString();
    this.cart$.subscribe(res => {
      cartProducts = res?.cart?.itemList || this.store.snapshot()?.cartStore?.cart?.itemList;
    });
    let customer = this.lsm.get('customer');
    const callCenterCustomer = this.lsm.get('customerCallCenter');
    const isSaG = this.lsm.get('mobileSaG');
    const isProvider = data.product.productType;
    customer = callCenterCustomer || customer;

    let params: any = {
      idCustomerWebSafe: customer.idCustomerWebSafe,
      idStoreGroup: this.locationManager.getIdStoreGroup(),
      quantity: data.quantity,
    };
    params = {
      ...params,
      id: data.product?.id ? data.product.id : '',
      isInShoppingCart: true,
      deliveryType: isProvider? 'PROVIDER' : isSaG ? 'SCANANDGO' : this.locationManager.getDeliveryType(),
    };
    if (isSaG && data.itemBarcode) {
      params.isInShoppingCart = !data.itemBarcode;
      params.deliveryType = DeliveryTypeEnum.SCANANDGO;
      params.itemBarcode = data.itemBarcode;
    }

    if (!isSaG) {
      params.origin = data?.product?.rsList || data?.product?.origin;
    }

    if (data.product) {
      if (data.product.onlyOnline) {
        this.onlineOnlyEvent.updateProductOnlineOnlyProduct({
          ...data.product,
          quantitySold: data.quantity,
        });
      }
      if (data.product.observations) {
        params.observations = data.product.observations;
      }
    }

    const nearbyStores = this.lsm.get('nearbyStores');

    body = {
      ...body,
      nearbyStores,
    }
   
    return this.cartService.addProduct(params, body).pipe(
      tap(res => {

        let payload;
        if (data.deliveryType) {
          payload = {product: new Product(res), quantity: data.quantity};
        } else {
          payload = {product: new Product(data.product), quantity: data.quantity};
        }

        this.updateProductAdvertisedEvent.updateProductAdvertised(res);

        const defaultQuantity = 1;
        const quantityDeleted = data.product.quantitySold - res.quantitySold;
        const previousQuantity =
          data.product.quantitySold > 0 ? data.product.quantitySold : defaultQuantity;
        const dataToUpdated = {action: data.action, quantity: data.quantity, quantityDeleted};
        this.updateCartAction(null, {...payload, ...dataToUpdated});
        //Notifica a GTM que se agrego un producto
        if (data.action === 'add' || data.action === undefined) {
          this.gtmService.onAddToCart({
            product: {
              ...data.product,
              quantitySold: data.quantity,
              quantityAdded: res.quantitySold - previousQuantity,
            },
            list: data.list,
            preprocessor: data.preprocessor,
            cartType: data.cartType,
          });
        }
        this.productToastEvent.updateProductToast({
          ...(payload.product ? payload.product : res),
          quantity: data.quantity,
        });
      }),
    );
  }

  removeProduct(data): Observable<any> {
    let customer = this.lsm.get('customer');
    const callCenterCustomer = this.lsm.get('customerCallCenter');
    const productID = data.product.id;
    customer = callCenterCustomer || customer;
    data.quantityDeleted = data.product.quantitySold;
    const params: DeleteProduct = {
      idStoreGroup: this.locationManager.getIdStoreGroup(),
      idCustomerWebSafe: customer.idCustomerWebSafe,
      idProduct: data.product.id,
    };

    return this.cartService
      .removeProduct(params)
      .pipe(
        tap(() => {
          this.updateCartAction('remove', data);
          this.cartDataUtil.removeProduct(productID);
        }),
        catchError(error => {
          console.error('Error removing product:', error);
          return of(null);
        })
      );
  }

  deleteCart(params): Observable<any> {
    let customer = this.lsm.get('customer');
    const callCenterCustomer = this.lsm.get('customerCallCenter');
    const isSaG = this.lsm.get('mobileSaG');
    customer = callCenterCustomer || customer;
    params = {
      ...params,
      idCustomerWebSafe: customer.idCustomerWebSafe,
      deliveryType: isSaG ? 'SCANANDGO' : 'EXPRESS',
    };
    return this.cartService.deleteCart(params).pipe(tap(() => this.deleteCartAction()));
  }

  getAdvertisedItems(): Observable<any> {
    const prop = 'advertisedItems';
    if (this.lsm.has(prop)) {
      const advertisedItems = this.lsm.get(prop);
      if (advertisedItems.expDate > new Date().getTime()) {
        return of(advertisedItems.hits);
      }
    }
    return this.cartService.getAdvertisedItems().pipe(
      switchMap(response => {
        if (response?.items) {
          const filters = `AND ( ${response.items.map(item => `id:${item}`).join(' OR ')} )`;
          return from(this.algoliaManager.search('', '*', '0', filters)).pipe(
            tap(res => {}),
            map(res => res.hits),
          );
        }
        return response;
      }),
    );
  }

  getHighDemand(): Observable<any> {
    return this.cartService.getHighDemand(this.getHighDemandParams()).pipe(
      tap(response => {
        const mandatory: boolean = Boolean(response?.highDemand?.mandatory);
        this.ssm.set('mandatory', mandatory);
      }), map(response => {
        if (!response.highDemand || (response.statusCode && response.statusCode !== 200)) {
          throw response;
        }
        return response.highDemand;
      }),
    );
  }

  getHighDemandVzla(): Observable<any> {
    return this.cartService.getHighDemandVzla(this.getHighDemandParams()).pipe(
      map(response => {
        if ((!response.highDemand || response.statusCode) && response.statusCode !== 200) {
          throw response;
        }
        return response.highDemand;
      }),
    );
  }

  private getHighDemandParams() {
    const params: any = {
      storeId: this.locationManager.getIdStoreGroup() || countryConfig.defaultStoreId,
    };
    if (!countryConfig.cartComponent.locationManager) {
      const location = this.locationManager.getLocation();
      if (location === undefined) {
        return null;
      } else {
        params['lat'] = location?.latitude;
        params['lng'] = location?.longitude;
        params['idCity'] = location?.city;
        params['municipality'] = location?.municipality;
      }
    }
    return params;
  }

  getSubstitutes(products): Observable<any> {
    let customer = this.lsm.get('customer');
    const callCenterCustomer = this.lsm.get('customerCallCenter');
    customer = callCenterCustomer || customer;
    if (this.locationManager.getDeliveryType() === DeliveryTypeEnum.EXPRESS) {
      const deliveryAddress = this.addressManager.getDeliveryAddress()
        ? {id: this.addressManager.getDeliveryAddress().idAddress}
        : {};
      const data = {
        idAddress: deliveryAddress.id,
        idCustomerWebSafe: customer.idCustomerWebSafe,
        items: products?.map(item => ({itemId: item.id, requestQuantity: item.quantitySold})),
      };
      return this.cartService
        .getSubstitutes(data, {keyClient: countryConfig.generalEnv.keyClient})
        .pipe(
          map(response => {
            if (response.itemsToSubstitute) {
              const productsFormat = [];
              response.toSubstitutes.forEach(substitutes => {
                if (substitutes.substitutesByList) {
                  substitutes.substitutesByList = substitutes.substitutesByList.map(
                    item => new Product(item),
                  );
                }
                productsFormat.push(substitutes);
              });
              return {...response, productsFormat};
            }
            return {...response};
        }), tap((response: SubstitutesRes) => {
          let dateFirstSchedule: string = response?.dateFirstSchedule;
          if (dateFirstSchedule) {
            dateFirstSchedule = dateFirstSchedule.replace('Z', '');
          }
          this.ssm.set('dateFirstSchedule', dateFirstSchedule);
          const forceToSchedule: boolean = Boolean(response?.forceToSchedule);
          this.ssm.set('forceToSchedule', forceToSchedule);
        }), catchError(() => {
          return of({error: true, message: 'Ocurrió un problema al cargar los sustitutos. Por favor, vuelve a intentarlo.'});
          }),
        );
    }
    return of(null);
  }

  handleSubstitutes(products) {
    const customer = this.lsm.get('customerCallCenter') || this.lsm.get('customer');
    const data: any = {
      deliveryType: this.locationManager.getDeliveryType(),
      idCustomerWebSafe: customer.idCustomerWebSafe,
      idStoreGroupFromRequest: this.locationManager.getIdStoreGroup(),
      isInShoppingCart: false,
      token: customer.token.token,
      tokenIdWebSafe: customer.token.tokenIdWebSafe,
      items: [],
      itemsToDelete: [],
    };
    const minItemQuantity = 0;

    for (const item of products) {
      if (item.optionSelectS === 1) {
        if (item.minQuantity > minItemQuantity) {
          data.items.push({
            substitute: true,
            itemId: item.itemId,
            quantityRequested: item.minQuantity,
          });
        }
        data.itemsToDelete.push({
          itemId: item.itemId,
        });
      }
      if (item.optionSelectS === 2) {
        data.items.push({
          substitute: false,
          itemId: item?.itemAdd?.id,
          quantityRequested: item.missingQuantity || item.quantityAddSustitute,
        });

        if (item.minQuantity > minItemQuantity) {
          data.items.push({
            substitute: true,
            itemId: item?.itemId,
            quantityRequested: item.minQuantity,
          });
        }

        data.itemsToDelete.push({
          itemId: item.itemId,
        });
      }
    }

    return this.cartService.handleSubstitutes(data);
  }

  getLocalCart() {
    const cart = this.ssm.get('cart');
    const sasCart = this.ssm.get('sasCart');
    if (sasCart && sasCart.amountCart > 0) {
      return sasCart;
    } else {
      return cart;
    }
  }

  merge(data): Observable<any> {
    data = {
      ...data,
      idStoreGroup: this.locationManager.getIdStoreGroup(),
      deliveryType: this.locationManager.getDeliveryType(),
    };
    return this.cartService.merge(data);
  }

  shoppingCart(data): Observable<any> {
    const customer = this.lsm.get('customer');
    data = {
      items: data,
      source: 'WEB',
      customerId: customer.id,
      deliveryType: this.locationManager.getDeliveryType(),
      storeId: this.locationManager.getIdStoreGroup(),
    };
    return this.cartService.shoppingCart(data);
  }
  getDeliveryType() {
    return this.locationManager.getDeliveryType();
  }

  postTip(data): Observable<TipResponse> {
    const customer = this.lsm.get('customer');
    const {tip, farmaCredits, paymentCardId} = data;
    this.farmacredits = farmaCredits;
    this.paymentCardId = paymentCardId;

    if (!customer?.token) { return of(null); }
    
    const dataTip: any = {
      deliveryType: this.locationManager.getDeliveryType(),
      idCustomerWebSafe: customer?.idCustomerWebSafe,
      idStoreGroup: this.locationManager.getIdStoreGroup(),
      token: customer?.token?.token,
      source: 'WEB',
      tokenIdWebSafe: customer.token.tokenIdWebSafe,
      id: customer.id,
      tip: tip,
      paymentCardId: paymentCardId || this.defaultPaymentCardId,
      talonOneData: {
        paymentCardId: paymentCardId || this.defaultPaymentCardId,
        farmaCredits: farmaCredits || this.defaultFarmaCredits,
        storeId: customer.idStoreGroup, 
        source: this.source, 
        deliveryType: this.locationManager.getDeliveryType(),
      },
      ...(data?.address || {})
    };
    return this.cartService.post(dataTip);
  }

  sendEventTalonOne(data): Observable<any> {
    return this.talonOne.sendEventTalonOne(data);
  }

  eventsTopsort(option): Observable<any> {
    return this.cartService.eventsTopsort(option);
  }

  getCreditsTalonOne(): Observable<Credits> {
    const customer = this.lsm.get('customer')?.id || 0;
    return this.talonOne.getCreditsTalonOne(customer)
  }
  getScheduleConfig(nearestStore?: number): Observable<ScheduleConfig> {
    const storeId = nearestStore || this.locationManager.getIdStoreGroup()
    const params = {};
    const dateFirstSchedule = this.ssm.get('dateFirstSchedule');
    if (dateFirstSchedule) {
      const date = new Date(dateFirstSchedule);
      
      if (!Number.isNaN(date.getTime())) {
        params['date-first-schedule'] = date.getTime();
      } else {
        console.warn('Invalid dateFirstSchedule', dateFirstSchedule);
      }
    }

    return this.cartService.getScheduleConfig(storeId, params);
  }

  getDeliveryHome():Observable<ResponseServiceOperationalInterface>{   
    return this.operationalService.getDeliveryHome();
  }

}
